import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Empty, Radio, Spin } from "antd";
import apiClient from "../../../helpers/apiClient";
import InsuranceOptionCard from "./InsuranceOptionCard";
import AxleInsuranceCard from "./AxleInsuranceCard";
import { getReservationPickupLocationId } from "../utils/reservationHelpers";

const InsuranceSelectionSection = ({
    reservation,
    selectedInsuranceId,
    onSelectedInsuranceIdChange,
    onSelectionStateChange,
    t,
}) => {
    const [insuranceAddonsData, setInsuranceAddonsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAxleCard, setShowAxleCard] = useState(false);
    const [axleTokenData, setAxleTokenData] = useState(null);
    const [isAxleVerifying, setIsAxleVerifying] = useState(false);
    const [removeAxleVerification, setRemoveAxleVerification] = useState(false);

    const isAxleVerified = Boolean(axleTokenData);

    const carId = reservation?.carSelected?._id || reservation?.carSelected?.id;
    const pickupLocationId = getReservationPickupLocationId(reservation);
    const isAxleOptionSelected = Boolean(showAxleCard || axleTokenData);
    const selectedInsuranceKey = axleTokenData ? "own" : selectedInsuranceId;

    const fetchInsuranceAddons = useCallback(async () => {
        if (!carId || !pickupLocationId) {
            setInsuranceAddonsData([]);
            return;
        }

        setLoading(true);
        try {
            const { data } = await apiClient.get(
                `/api/addons-insurance/available?carId=${carId}&pickupLocationId=${pickupLocationId}`,
            );
            setInsuranceAddonsData(data?.success && data?.data ? data.data.result || [] : []);
        } catch {
            setInsuranceAddonsData([]);
        } finally {
            setLoading(false);
        }
    }, [carId, pickupLocationId]);

    useEffect(() => {
        fetchInsuranceAddons();
    }, [fetchInsuranceAddons]);

    useEffect(() => {
        onSelectionStateChange({
            insuranceAddonsData,
            selectedInsuranceKey,
            axleTokenData,
            isAxleVerifying,
            isAxleOptionSelected,
        });
    }, [
        axleTokenData,
        insuranceAddonsData,
        isAxleOptionSelected,
        isAxleVerifying,
        onSelectionStateChange,
        selectedInsuranceKey,
    ]);

    const handleSelectInsurance = (addonId) => {
        onSelectedInsuranceIdChange(addonId);
        setRemoveAxleVerification(true);
        setAxleTokenData(null);
        setShowAxleCard(false);
        setIsAxleVerifying(false);
    };

    const handleToggleOwnInsurance = () => {
        const isOwnSelected = Boolean(showAxleCard || axleTokenData);

        if (isOwnSelected) {
            setShowAxleCard(false);
            setAxleTokenData(null);
            setRemoveAxleVerification(true);
            onSelectedInsuranceIdChange(null);
            return;
        }

        setShowAxleCard(true);
        onSelectedInsuranceIdChange(null);
    };

    const handleAxleTokenData = (data) => {
        setAxleTokenData(data);
        if (data) {
            onSelectedInsuranceIdChange(null);
        }
    };

    const hasInsuranceOptions = insuranceAddonsData.length > 0 || true;

    return (
        <section className="space-y-4">
            <div>
                <h2 className="fontSemi text-xl text-[#222222] md:text-[22px]">{t("chooseCoverageTitle")}</h2>
                <p className="fontRegular mt-2 text-sm leading-6 text-[#7F8796]">
                    {t("chooseCoverageSubtitle")}
                </p>
            </div>

            {loading ? (
                <div className="flex justify-center py-8">
                    <Spin />
                </div>
            ) : !hasInsuranceOptions ? (
                <Empty description={t("noInsuranceOptions")} />
            ) : (
                <div className="space-y-3">
                    <div
                        className={`w-full rounded-xl border transition-colors ${
                            showAxleCard || axleTokenData
                                ? "border-[#5D5DF6] bg-[#5D5DF6]/[0.03]"
                                : "border-[#E6EAF2] bg-white"
                        }`}>
                        <button
                            type="button"
                            onClick={handleToggleOwnInsurance}
                            className="flex w-full items-start gap-3 p-4 text-left">
                                <Radio
                                    checked={Boolean(showAxleCard || axleTokenData)}
                                    className="!mt-0.5 !shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="fontSemi text-base text-[#222222]">
                                        {t("ownInsuranceTitle")}
                                    </p>
                                    <p className="fontRegular mt-1 text-sm text-[#7F8796]">
                                        {t("ownInsuranceHint")}
                                    </p>
                                </div>
                            </button>

                            {showAxleCard || isAxleVerified ? (
                                <div className="border-t border-[#EEF0F6] px-4 py-4">
                                    <AxleInsuranceCard
                                        reservation={reservation}
                                        isAxleVerifying={isAxleVerifying}
                                        setIsAxleVerifying={setIsAxleVerifying}
                                        onTokenData={handleAxleTokenData}
                                        removeVerification={removeAxleVerification}
                                        onRemoveVerificationHandled={() => setRemoveAxleVerification(false)}
                                        t={t}
                                    />
                                </div>
                            ) : null}
                        </div>

                    {insuranceAddonsData.map((item) => (
                        <InsuranceOptionCard
                            key={item.id}
                            item={item}
                            selected={selectedInsuranceId === item.id}
                            onSelect={() => {
                                if (selectedInsuranceId === item.id) {
                                    onSelectedInsuranceIdChange(null);
                                } else {
                                    handleSelectInsurance(item.id);
                                }
                            }}
                            t={t}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

InsuranceSelectionSection.propTypes = {
    reservation: PropTypes.object,
    selectedInsuranceId: PropTypes.string,
    onSelectedInsuranceIdChange: PropTypes.func.isRequired,
    onSelectionStateChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export default InsuranceSelectionSection;
